import { useEffect, useState } from "react"

import { MessageCircleQuestion, SquareIcon, RatioIcon } from "lucide-react"
import { Select, DefaultTooltip, ToggleOptionGroup, ToggleOptionGroupItem, InputGroup, InputGroupText } from "twyne-new.ui"

import { hasKey } from "../../utils"
import { UNIT_OPTIONS } from "../../utils/predefined_sizes"
import { PredefinedArbitraryUnit } from "../PredefinedArbitraryUnit"
import { ListContent, ListItem, ListLabel, ListWrapper } from "../components/list"

export default function Advanced({ values, onChange }) {
  if (!values) {
    return null
  }

  const { clip, composite, mode, origin, position, repeat, size, type } = values

  return <ListWrapper>
    <div className='flex flex-col gap-2'>

      <ListItem>
        <CustomListLabel title='Position' />
        <ListContent>
          <AxisInputs
            values={position}
            onChange={position => onChange({ position })}
            options={POSITION}
            axisOptions={[{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }, { value: 'top', label: 'Top' }, { value: 'bottom', label: 'Bottom' }]}
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Size' />
        <ListContent>
          <AxisInputs
            values={size}
            onChange={size => onChange({ size })}
            options={SIZE}
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Clip' />
        <ListContent>
          <Select
            options={CLIP}
            value={clip}
            onChange={option => onChange({ clip: option?.value })}
            variant='accent'
            clearable
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Composite' />
        <ListContent>
          <Select
            options={COMPOSITE}
            value={composite}
            onChange={option => onChange({ composite: option?.value })}
            variant='accent'
            clearable
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Mode' />
        <ListContent>
          <Select
            options={MODE}
            value={mode}
            onChange={option => onChange({ mode: option?.value })}
            variant='accent'
            clearable
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Origin' />
        <ListContent>
          <Select
            options={ORIGIN}
            value={origin}
            onChange={option => onChange({ origin: option?.value })}
            variant='accent'
            clearable
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Repeat' />
        <ListContent>
          <Select
            options={REPEAT}
            value={repeat}
            onChange={option => onChange({ repeat: option?.value })}
            variant='accent'
            clearable
          />
        </ListContent>
      </ListItem>

      <ListItem>
        <CustomListLabel title='Type' />
        <ListContent>
          <Select
            options={TYPE}
            value={type}
            onChange={option => onChange({ type: option?.value })}
            variant='accent'
            clearable
          />
        </ListContent>
      </ListItem>

    </div>
  </ListWrapper>
}

const AxisInputs = ({ values, onChange, options, axisOptions = options }) => {
  const [type, setType] = useState()

  useEffect(() => {
    if (type === undefined) {
      setType(hasKey(values, 'x') || hasKey(values, 'y') ? 'axis' : 'all')
    }
  }, [type, values, setType])

  const handleChangeType = value => {
    if (!value) {
      return
    }

    setType(value)
    onChange({})
  }

  return <>
    <div className='flex flex-col gap-1'>
      <ToggleOptionGroup size='lg' value={type || ''} type='single' onValueChange={handleChangeType}>
        <ToggleOptionGroupItem value='all'>
          <DefaultTooltip content='Set for X and Y at the same time' triggerProps={{ className: 'w-full flex justify-center' }}>
            <SquareIcon size='16' />
          </DefaultTooltip>
        </ToggleOptionGroupItem>
        <ToggleOptionGroupItem value='axis'>
          <DefaultTooltip content='Set for X and Y separately' triggerProps={{ className: 'w-full flex justify-center' }}>
            <RatioIcon size='16' />
          </DefaultTooltip>
        </ToggleOptionGroupItem>
      </ToggleOptionGroup>

      {type === 'all' && <>
        <PredefinedArbitraryUnit
          predefined
          predefinedOptions={options}
          unitOptions={UNIT_OPTIONS}
          defaultUnit='px'
          value={values?.value}
          onChange={value => onChange({ ...values, value })}
        />
      </>}

      {type === 'axis' && <>
        <div className='flex flex-col gap-1 w-full'>
          <InputGroup variant='accent' className='w-full'>
            <InputGroupText addonType='prepend'>
              X
            </InputGroupText>
            <PredefinedArbitraryUnit
              predefined
              predefinedOptions={axisOptions}
              unitOptions={UNIT_OPTIONS}
              defaultUnit='px'
              value={values?.x}
              onChange={value => onChange({ ...values, x: value })}
            />
          </InputGroup>
          <InputGroup variant='accent' className='w-full'>
            <InputGroupText addonType='prepend'>
              Y
            </InputGroupText>
            <PredefinedArbitraryUnit
              predefined
              predefinedOptions={axisOptions}
              unitOptions={[{ label: 'Unitless', value: 'unitless' }, ...UNIT_OPTIONS]}
              defaultUnit='unitless'
              value={values?.y}
              onChange={value => onChange({ ...values, y: value })}
            />
          </InputGroup>
        </div>
      </>}
    </div>
  </>
}

const CustomListLabel = ({ title, hint }) => <>
  <ListLabel>
    <p>{title}</p>
    {hint && <>
      <DefaultTooltip side='right' contentProps={{ className: 'max-w-40' }} content={hint}>
        <MessageCircleQuestion size='12' />
      </DefaultTooltip>
    </>}
  </ListLabel>
</>

const CLIP = [
  { label: 'Border', value: 'clip-border' },
  { label: 'Padding', value: 'clip-padding' },
  { label: 'Content', value: 'clip-content' },
  { label: 'Fill', value: 'clip-fill' },
  { label: 'Stroke', value: 'clip-stroke' },
  { label: 'View', value: 'clip-view' },
  { label: 'None', value: 'no-clip' },
]

const COMPOSITE = [
  { label: 'Add', value: 'add' },
  { label: 'Subtract', value: 'subtract' },
  { label: 'Intersect', value: 'intersect' },
  { label: 'Exclude', value: 'exclude' },
]

const MODE = [
  { label: 'Alpha', value: 'alpha' },
  { label: 'Luminosity', value: 'luminosity' },
  { label: 'Match', value: 'match' },
]

const ORIGIN = [
  { label: 'Alpha', value: 'border' },
  { label: 'Luminosity', value: 'padding' },
  { label: 'Match', value: 'content' },
  { label: 'Fill', value: 'fill' },
  { label: 'Stroke', value: 'stroke' },
  { label: 'View', value: 'view' },
]

const REPEAT = [
  { label: 'Repeat', value: 'repeat' },
  { label: 'No Repeat', value: 'no-repeat' },
  { label: 'Repeat only X', value: 'repeat-x' },
  { label: 'Repeat only Y', value: 'repeat-y' },
  { label: 'Space', value: 'repeat-space' },
  { label: 'Round', value: 'repeat-round' },
]

const SIZE = [
  { label: 'Auto', value: 'auto' },
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
]

export const POSITION = [
  { label: 'Top Left', value: 'top-left' },
  { label: 'Top', value: 'top' },
  { label: 'Top Right', value: 'top-right' },
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
  { label: 'Bottom Left', value: 'bottom-left' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Bottom Right', value: 'bottom-right' },
]

export const TYPE = [
  { label: 'Alpha', value: 'alpha' },
  { label: 'Luminance', value: 'luminance' },
]

